import React, { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Envelope, MapPin, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteSection() {
  const { t } = useLanguage();
  const f = t.quote.form;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "", material_type: "", color_preference: "", description: "" });

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type || !form.description) {
      toast.error(f.validation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success(f.success);
      setForm({ name: "", email: "", phone: "", service_type: "", material_type: "", color_preference: "", description: "" });
    } catch {
      toast.error(f.error);
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = "bg-transparent border-white/20 text-white placeholder:text-zinc-500 focus-visible:ring-[#FFD700] focus-visible:border-[#FFD700] h-12";
  const labelStyles = "text-xs uppercase tracking-wider text-zinc-400 mb-2 block";

  return (
    <section id="cotizacion" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="quote-section">
      <Toaster theme="dark" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700] mb-4 font-light">{t.quote.overline}</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl font-light text-white tracking-tight mb-8">{t.quote.title}</h2>
            <p className="text-base text-zinc-300 font-light leading-relaxed mb-12">{t.quote.description}</p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#FFD700]/30"><Phone size={20} weight="duotone" className="text-[#FFD700]" /></div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Daniel Salas</p>
                  <a href="tel:9732883496" className="text-white hover:text-[#FFD700] transition-colors">973-288-3496</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#25D366]/30"><WhatsappLogo size={20} weight="duotone" className="text-[#25D366]" /></div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">{t.quote.whatsapp}</p>
                  <a href="https://wa.me/18622714917" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#25D366] transition-colors">862-271-4917</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#FFD700]/30"><Envelope size={20} weight="duotone" className="text-[#FFD700]" /></div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">{t.quote.email}</p>
                  <a href="mailto:dominicanupholstered.nj@gmail.com" className="text-white hover:text-[#FFD700] transition-colors text-sm">dominicanupholstered.nj@gmail.com</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#FFD700]/30"><MapPin size={20} weight="duotone" className="text-[#FFD700]" /></div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">{t.quote.location}</p>
                  <a href="https://maps.google.com/?q=1045+North+Broad+St+Elizabeth+NJ+07208" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FFD700] transition-colors text-sm">1045 North Broad St. Elizabeth, NJ 07208</a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/10 p-8 md:p-10 space-y-6" data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className={labelStyles}>{f.name}</label><Input className={inputStyles} placeholder={f.namePlaceholder} value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="quote-input-name" /></div>
                <div><label className={labelStyles}>{f.email}</label><Input type="email" className={inputStyles} placeholder={f.emailPlaceholder} value={form.email} onChange={(e) => handleChange("email", e.target.value)} data-testid="quote-input-email" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className={labelStyles}>{f.phone}</label><Input type="tel" className={inputStyles} placeholder={f.phonePlaceholder} value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} data-testid="quote-input-phone" /></div>
                <div><label className={labelStyles}>{f.serviceType}</label>
                  <Select onValueChange={(v) => handleChange("service_type", v)} value={form.service_type}>
                    <SelectTrigger className={inputStyles} data-testid="quote-select-service"><SelectValue placeholder={f.selectService} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                      {t.quote.serviceOptions.map((opt) => <SelectItem key={opt.value} value={opt.value} className="focus:bg-[#FFD700]/10 focus:text-[#FFD700]">{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className={labelStyles}>{f.materialType}</label>
                  <Select onValueChange={(v) => handleChange("material_type", v)} value={form.material_type}>
                    <SelectTrigger className={inputStyles} data-testid="quote-select-material"><SelectValue placeholder={f.selectMaterial} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                      {t.quote.materialOptions.map((opt) => <SelectItem key={opt.value} value={opt.value} className="focus:bg-[#FFD700]/10 focus:text-[#FFD700]">{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className={labelStyles}>{f.colorPref}</label><Input className={inputStyles} placeholder={f.colorPlaceholder} value={form.color_preference} onChange={(e) => handleChange("color_preference", e.target.value)} data-testid="quote-input-color" /></div>
              </div>
              <div><label className={labelStyles}>{f.description}</label><Textarea className={`${inputStyles} min-h-[120px]`} placeholder={f.descPlaceholder} value={form.description} onChange={(e) => handleChange("description", e.target.value)} data-testid="quote-input-description" /></div>
              <button type="submit" disabled={loading} className="w-full py-4 bg-[#FFD700] text-black font-medium uppercase tracking-wider text-sm hover:bg-[#E5C100] transition-all duration-300 disabled:opacity-50" data-testid="quote-submit-btn">{loading ? f.sending : f.submit}</button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
